import { memo, useEffect, useState } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';
import { SxProps, Theme } from '@mui/material/styles';
import { type DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import dayjs, { type Dayjs } from 'dayjs';
import { LabelWithTooltip } from '../components/LabelWithTooltip';
import { DynamicFieldType, TDynamicFieldOnChange } from '../DynamicFormField';

/**
 * ------------------------------------------
 *  Date Range Dynamic Field
 *  - Handles the `dateRangeFilter` group field
 *
 *  Stabilized manual date entry:
 *  - Only propagates valid, complete dates upstream (no clearing on partial input)
 *  - Tracks a local display value that updates immediately to prevent the picker
 *    from flashing back to the old date during the debounce window
 *  - Debounces the upstream form commit so calendar panels don't jump on every
 *    keystroke while the user is still typing
 * ------------------------------------------
 */

type DateRangeFieldProps = {
  fieldConfig: DynamicFieldType;
  selectedValue: any;
  disabled: boolean;
  handleValueChange: TDynamicFieldOnChange;
  tooltipText: string | null;
  fieldSx?: SxProps<Theme>;
};

export const DateRangeField = memo(
  ({
    fieldConfig,
    selectedValue,
    disabled,
    handleValueChange,
    tooltipText,
    fieldSx,
  }: DateRangeFieldProps) => {
    // Local display state - updates immediately on valid input so the picker never
    // flashes the old date while the debounced upstream commit is still pending.
    const [localDateRange, setLocalDateRange] = useState<DateRange<Dayjs> | null>(() => {
      if (selectedValue) {
        return [dayjs(selectedValue[0]), dayjs(selectedValue[1])];
      }
      return null;
    });

    // Keep local display state in sync when selectedValue changes from outside this
    // component (e.g. Month / Week quick-select buttons, form reset).
    useEffect(() => {
      if (!selectedValue) {
        return;
      }
      setLocalDateRange([dayjs(selectedValue[0]), dayjs(selectedValue[1])]);
    }, [selectedValue]);

    // Debounce the upstream form commit so calendar panels don't jump on every
    // keystroke while the user is still typing a date.
    const debouncedCommit = useDebouncedCallback(
      (name: string, newValue: any) => handleValueChange(name, newValue),
      200,
    );

    return (
      <DateRangePicker
        data-testid="dynamic-field-date-range-filter-group"
        label={
          <LabelWithTooltip
            label={fieldConfig.label}
            tooltip={tooltipText}
          />
        }
        value={localDateRange ?? undefined}
        disabled={disabled}
        maxDate={fieldConfig?.config?.maxDate}
        onChange={(newValue: [Dayjs | null, Dayjs | null]) => {
          const [start, end] = newValue ?? [null, null];
          // Only propagate when both dates are fully valid
          if (start?.isValid() && end?.isValid()) {
            // Immediately sync the local display value
            setLocalDateRange([start, end]);
            // Debounce the upstream commit
            debouncedCommit(fieldConfig.name, [
              start.format('YYYY-MM-DD'),
              end.format('YYYY-MM-DD'),
            ]);
          }
        }}
        slotProps={{
          textField: {
            fullWidth: true,
            variant: 'outlined',
            InputLabelProps: { shrink: true, sx: { zIndex: 0 } },
            sx: {
              // Match other dynamic fields' height.
              '& .MuiPickersSectionList-root': { py: 1.1 },
              ...fieldSx,
            },
          },
        }}
      />
    );
  },
);

DateRangeField.displayName = 'DateRangeField';
